import { type JSX, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv, type VariantProps } from '../lib/tv.ts'

/**
 * Quantities and money are STRINGS all the way to the DOM — see AGENTS.md ground rule 4.
 * Floating point cannot reconcile an invoice, so nothing here coerces.
 *
 * Monospace rather than Manrope-with-tabular-figures: an accountant checking a column of sums is
 * matching glyph shapes, and a proportional face makes `8` and `3` too similar at 13px.
 */
const decimalDisplay = tv({
  base: 'block font-mono text-body tabular-nums',
  variants: {
    align: {
      right: 'text-right',
      left: 'text-left',
    },
    tone: {
      /** The default. A figure is not more important than its label. */
      normal: 'text-text',
      muted: 'text-text-subtle',
      /** A total, or the one figure a screen exists to show. */
      strong: 'font-medium text-text',
      /** Model output nobody has confirmed yet. */
      draft: 'text-draft',
    },
  },
  defaultVariants: { align: 'right', tone: 'normal' },
})

interface DecimalDisplayProps
  extends Omit<JSX.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof decimalDisplay> {
  value: string
}

export type MoneyProps = DecimalDisplayProps
export type QuantityProps = DecimalDisplayProps

const DecimalDisplay = (props: DecimalDisplayProps) => {
  const [local, rest] = splitProps(props, ['value', 'align', 'tone', 'class'])

  // Decimal strings must reach the DOM unchanged; numeric coercion would lose accounting precision.
  return (
    <span
      {...rest}
      class={cn(decimalDisplay({ align: local.align, tone: local.tone }), local.class)}
    >
      {local.value}
    </span>
  )
}

export const Money = (props: MoneyProps) => <DecimalDisplay {...props} />

export const Quantity = (props: QuantityProps) => <DecimalDisplay {...props} />
